import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templateRulesService } from '../services/template-rules.service';
import type { Rule, EventType } from '../types/rule.types';

const KEYS = {
  rules: (tenantId: string) => ['template-rules', tenantId] as const,
  rulesForTemplate: (tenantId: string, templateId: string) => ['template-rules', tenantId, 'template', templateId] as const,
  rule: (tenantId: string, ruleId: string) => ['template-rules', tenantId, ruleId] as const,
  versions: (tenantId: string, ruleId: string) => ['template-rules', tenantId, ruleId, 'versions'] as const,
  eventTypes: (tenantId: string) => ['template-rules', tenantId, 'catalog', 'event-types'] as const,
  properties: (tenantId: string) => ['template-rules', tenantId, 'catalog', 'properties'] as const,
};

export function useRulesForTemplate(tenantId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: KEYS.rulesForTemplate(tenantId ?? '', templateId ?? ''),
    queryFn: () => templateRulesService.listForTemplate(tenantId!, templateId!),
    enabled: !!tenantId && !!templateId,
    staleTime: 30_000,
  });
}

export function useRulesForTenant(tenantId: string | undefined) {
  return useQuery({
    queryKey: KEYS.rules(tenantId ?? ''),
    queryFn: () => templateRulesService.listForTenant(tenantId!),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useRuleVersions(tenantId: string | undefined, ruleId: string | undefined) {
  return useQuery({
    queryKey: KEYS.versions(tenantId ?? '', ruleId ?? ''),
    queryFn: () => templateRulesService.listVersions(tenantId!, ruleId!),
    enabled: !!tenantId && !!ruleId,
    staleTime: 30_000,
  });
}

export function useCreateRule(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof templateRulesService.createRule>[1]) =>
      templateRulesService.createRule(tenantId, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: KEYS.rules(tenantId) });
      qc.invalidateQueries({ queryKey: KEYS.rulesForTemplate(tenantId, variables.templateId) });
    },
  });
}

export function useUpdateRule(tenantId: string, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, ...data }: { ruleId: string } & Parameters<typeof templateRulesService.updateRule>[2]) =>
      templateRulesService.updateRule(tenantId, ruleId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.rules(tenantId) });
      qc.invalidateQueries({ queryKey: KEYS.rulesForTemplate(tenantId, templateId) });
    },
  });
}

export function useDeleteRule(tenantId: string, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) =>
      templateRulesService.deleteRule(tenantId, ruleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.rules(tenantId) });
      qc.invalidateQueries({ queryKey: KEYS.rulesForTemplate(tenantId, templateId) });
    },
  });
}

export function useSimulateRule(tenantId: string) {
  return useMutation({
    mutationFn: ({ ruleId, ...data }: { ruleId: string; testData: Record<string, unknown>; eventType: EventType }) =>
      templateRulesService.simulate(tenantId, ruleId, data),
  });
}

export function useDetectConflicts(tenantId: string) {
  return useMutation({
    mutationFn: (data: { templateId: string; ruleIds?: string[] }) =>
      templateRulesService.detectConflicts(tenantId, data),
  });
}

export function useEventTypes(tenantId: string | undefined) {
  return useQuery({
    queryKey: KEYS.eventTypes(tenantId ?? ''),
    queryFn: () => templateRulesService.getEventTypes(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes — rarely changes
  });
}

export function usePropertyCatalog(tenantId: string | undefined) {
  return useQuery({
    queryKey: KEYS.properties(tenantId ?? ''),
    queryFn: () => templateRulesService.getProperties(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}
